import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from 'contexts/AuthContext'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import { db } from 'config/firebase'
import {
  collection,
  deleteDoc,
  doc,
  DocumentData,
  DocumentReference,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
} from 'firebase/firestore'
import { addInventoryDocument, DuplicateInventoryError } from 'core/inventory/addInventoryDocument'
import isEqual from 'lodash/isEqual'
import { useCallback, useEffect, useState } from 'react'
import { onSnapshot } from 'firebase/firestore'
import { Vehicle } from '@otw/models'
import { vehicleConverter } from '@otw/models/converters'
import { fetchMasterInventory, sortInventoryByDateAdded } from './fetchMasterInventory'
import { ACTION_BANNER_QUERY_ROOT } from './actionBannerQueries'

interface UseInventoryOptions {
  onSuccess?: () => void
  onError?: (error: Error) => void
  dontShowUpdates?: boolean
  /** Skip the full-collection snapshot. Dashboard screens should set this. */
  skipRealtime?: boolean
  /** When false, skip the collection getDocs (mutations still work). */
  enabled?: boolean
}

interface UpdateDocumentParams {
  id: string
  data: Partial<DocumentData>
}

// Add this interface for filter state

interface HistoryEntry {
  id: string
  documentId: string
  timestamp: any
  alphas?: Record<string, any>
  deltas?: Record<string, any>
  fullData?: Record<string, any>
  modifiedBy: string
  collection: string
  type: 'update' | 'create' | 'delete'
  identifier: string
  docId: string
}

type InventorySnapshotEntry = {
  refCount: number
  unsubscribe: () => void
}

const inventorySnapshots = new Map<string, InventorySnapshotEntry>()

const inventorySnapshotKey = (
  inventoryCollection: string,
  subcollection?: { name: string; parentDocId: string },
) =>
  subcollection
    ? `${inventoryCollection}/${subcollection.parentDocId}/${subcollection.name}`
    : inventoryCollection

export const useInventory = (
  inventoryCollection: 'imported_vehicles' | 'master_inventory' | 'shield_inventory',
  subcollection?: {
    name: string
    parentDocId: string
  },
  options?: UseInventoryOptions,
) => {
  const { showSuccess, showError, showInfo, showWarning } = useSnackbar()
  const queryClient = useQueryClient()
  const { userInfo, currentUser } = useAuth()

  const [isSnapshotListenerActive, setIsSnapshotListenerActive] = useState(
    () => !options?.skipRealtime && !options?.dontShowUpdates,
  )

  // Create collection ref based on whether subcollection is specified
  const collectionRef = subcollection
    ? collection(db, inventoryCollection, subcollection.parentDocId, subcollection.name)
    : collection(db, inventoryCollection)

  const queryKey = subcollection
    ? [inventoryCollection, subcollection.parentDocId, subcollection.name]
    : [inventoryCollection]

  const {
    data: vehicles,
    isLoading: isQueryLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: async (): Promise<Vehicle[]> => {
      const cached = queryClient.getQueryData<Vehicle[]>(queryKey)
      if (cached && cached.length > 0) {
        return cached
      }
      if (!subcollection && inventoryCollection === 'master_inventory') {
        return fetchMasterInventory()
      }
      const collectionWithConverter = collectionRef.withConverter(vehicleConverter)
      const snapshot = await getDocs(collectionWithConverter)
      const docs = snapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id,
      }))
      return sortInventoryByDateAdded(docs)
    },
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    enabled: options?.enabled !== false && !isSnapshotListenerActive,
  })

  const isLoading = isQueryLoading || (isSnapshotListenerActive && vehicles === undefined)

  const refetchVehicles = useCallback(async () => {
    await refetch()
  }, [refetch])

  const invalidateActionBanner = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: [ACTION_BANNER_QUERY_ROOT] })
  }, [queryClient])

  const addDocument = useMutation({
    mutationFn: async (data: DocumentData) => {
      return addInventoryDocument(collectionRef, { ...data, entered_by: userInfo?.email })
    },
    onSuccess: () => {
      if (!options?.dontShowUpdates) {
        showSuccess('Document added successfully')
      }
      queryClient.invalidateQueries({ queryKey })
      invalidateActionBanner()
      options?.onSuccess?.()
    },
    onError: (error: Error) => {
      if (error instanceof DuplicateInventoryError) {
        showError(error.message)
      } else if (!options?.dontShowUpdates) {
        showError('Error adding document')
      }
      console.error('Error adding document:', error)
      options?.onError?.(error)
    },
  })

  // One shared onSnapshot per collection — extra mounts only bump a refcount.
  useEffect(() => {
    if (!isSnapshotListenerActive) return

    const key = inventorySnapshotKey(inventoryCollection, subcollection)
    const existing = inventorySnapshots.get(key)
    if (existing) {
      existing.refCount += 1
      return () => {
        const entry = inventorySnapshots.get(key)
        if (!entry) return
        entry.refCount -= 1
        if (entry.refCount <= 0) {
          entry.unsubscribe()
          inventorySnapshots.delete(key)
        }
      }
    }

    const collectionWithConverter = collectionRef.withConverter(vehicleConverter)

    const unsubscribe = onSnapshot(
      collectionWithConverter,
      snapshot => {
        const source = snapshot.metadata.fromCache ? 'cache' : 'server'
        const hasPendingWrites = snapshot.metadata.hasPendingWrites
        if (source === 'server' && hasPendingWrites) return

        const updatedDocs = snapshot.docs.map(doc => ({
          ...doc.data(),
          id: doc.id,
        }))

        queryClient.setQueryData(queryKey, updatedDocs)
      },
      (error) => {
        console.error('❌ [useInventory] onSnapshot error:', error)
      }
    )

    inventorySnapshots.set(key, { refCount: 1, unsubscribe })

    return () => {
      const entry = inventorySnapshots.get(key)
      if (!entry) return
      entry.refCount -= 1
      if (entry.refCount <= 0) {
        entry.unsubscribe()
        inventorySnapshots.delete(key)
      }
    }
  }, [inventoryCollection, subcollection, isSnapshotListenerActive, queryClient])

  const updateDocument = useMutation({
    mutationFn: async ({ id, data, isRevert = false }: UpdateDocumentParams & { isRevert?: boolean }) => {
      const docRef = doc(collectionRef, id) as DocumentReference

      if (isRevert) {
        await updateDoc(docRef, {
          ...data,
          modified_by: userInfo?.email,
          updated_by: currentUser?.uid,
          updated_at: new Date().toISOString(),
        })
        return { id, data }
      }

      const docSnap = await getDoc(docRef)
      const currentData = docSnap.data()

      // Recursively remove undefined values - Firestore rejects undefined at any nesting level
      const removeUndefined = (obj: any): any => {
        if (obj === undefined) return undefined
        if (obj === null) return null
        if (Array.isArray(obj)) {
          return obj.map(removeUndefined).filter((v) => v !== undefined)
        }
        if (typeof obj === 'object') {
          return Object.fromEntries(
            Object.entries(obj)
              .filter(([, v]) => v !== undefined)
              .map(([k, v]) => [k, removeUndefined(v)])
          )
        }
        return obj
      }

      // Sanitize data to remove any undefined values which Firestore doesn't accept
      const sanitizedData = Object.entries(data).reduce(
        (acc, [key, value]) => {
          // Skip undefined values
          if (value === undefined) return acc
          const cleaned = removeUndefined(value)
          if (cleaned === undefined) return acc
          // Convert null values to empty strings for string fields
          if (cleaned === null && typeof currentData?.[key] === 'string') {
            acc[key] = ''
          } else {
            acc[key] = cleaned
          }
          return acc
        },
        {} as Record<string, any>,
      )

      // Now check what actually changed from current data
      const actualChanges = Object.entries(sanitizedData).reduce(
        (acc, [key, value]) => {
          // For complex objects like sold_to_user or sold_by_user, convert to JSON for deep comparison
          // or use a more robust comparison when needed
          const currentValue = currentData?.[key]

          if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            // For object values, do a deeper comparison
            const valueKeys = Object.keys(value)

            // If the current value is not an object or is null/undefined, consider this a change
            if (!currentValue || typeof currentValue !== 'object') {
              acc[key] = value
            } else {
              // Check each property in the object
              let hasChanged = false

              for (const subKey of valueKeys) {
                if (value[subKey] !== currentValue[subKey]) {
                  hasChanged = true
                  break
                }
              }

              if (hasChanged) {
                acc[key] = value
              }
            }
          } else if (!isEqual(currentValue, value)) {
            // For non-objects, use lodash's isEqual for comparison
            acc[key] = value
          }

          return acc
        },
        {} as Record<string, any>,
      )

      if (Object.keys(actualChanges).length > 0) {
        // Add metadata
        const updatedData = {
          ...actualChanges,
          modified_by: userInfo?.email,
          updated_by: currentUser?.uid,
          updated_at: new Date().toISOString(),
        }

        // Perform the update
        await updateDoc(docRef, updatedData)
      }

      return { id, data: actualChanges }
    },
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({ queryKey })
      const previousInventory = queryClient.getQueryData(queryKey)

      queryClient.setQueryData(queryKey, (old: Vehicle[] | undefined) => {
        if (!old) return old
        return old.map((item: Vehicle) => (item.id === id ? { ...item, ...data } : item))
      })

      return { previousInventory }
    },
    onError: (err, variables, context) => {
      if (context?.previousInventory) {
        queryClient.setQueryData(queryKey, context.previousInventory)
      }
      if (!options?.dontShowUpdates) {
        showError('Error updating document')
      }
      console.error('Error updating document:', err)
      options?.onError?.(err)
    },
    onSuccess: result => {
      if (!options?.dontShowUpdates) {
        showSuccess('Changes saved successfully')
      }
      invalidateActionBanner()
      options?.onSuccess?.()
    },
  })

  const deleteDocument = useMutation({
    mutationFn: async (id: string) => {
      const docRef = doc(collectionRef, id)
      await deleteDoc(docRef)
    },
    onSuccess: () => {
      if (!options?.dontShowUpdates) {
        showSuccess('Document deleted successfully')
      }
      queryClient.invalidateQueries({ queryKey })
      invalidateActionBanner()
      options?.onSuccess?.()
    },
    onError: (error: Error) => {
      if (!options?.dontShowUpdates) {
        showError('Error deleting document')
      }
      console.error('Error deleting document:', error)
      options?.onError?.(error)
    },
  })

  const bulkDeleteDocuments = useMutation({
    mutationFn: async (ids: string[]) => {
      await Promise.all(ids.map(id => deleteDoc(doc(collectionRef, id))))
    },
    onSuccess: () => {
      if (!options?.dontShowUpdates) {
        showSuccess('Documents deleted successfully')
      }
      queryClient.invalidateQueries({ queryKey })
      invalidateActionBanner()
      options?.onSuccess?.()
    },
    onError: (error: Error) => {
      if (!options?.dontShowUpdates) {
        showError('Error deleting documents')
      }
      console.error('Error deleting documents:', error)
      options?.onError?.(error)
    },
  })

  const handleCellValueChange = useCallback(
    (id: string, field: string, value: any) => {
      updateDocument.mutate({
        id,
        data: { [field]: value },
      })
    },
    [updateDocument, queryClient],
  )

  const restoreDocumentMutation = useMutation({
    mutationFn: async (data: { id: string; fullData: Record<string, any> }) => {
      const { id, fullData } = data
      await setDoc(doc(collectionRef, id), fullData)
    },
    onSuccess: () => {
      if (!options?.dontShowUpdates) {
        showSuccess('Document restored successfully')
      }
      queryClient.invalidateQueries({ queryKey })
      invalidateActionBanner()
      options?.onSuccess?.()
    },
    onError: (error: Error) => {
      if (!options?.dontShowUpdates) {
        showError('Error restoring document')
      }
      console.error('Error restoring document:', error)
      options?.onError?.(error)
    },
  })

  return {
    vehicles,
    isLoading,
    isError,
    error,
    addDocument: addDocument.mutate,
    addDocumentAsync: addDocument.mutateAsync,
    updateDocument: updateDocument.mutate,
    updateDocumentAsync: updateDocument.mutateAsync,
    deleteDocument: deleteDocument.mutate,
    bulkDeleteDocuments: bulkDeleteDocuments.mutate,
    handleCellValueChange,
    queryClient,
    refetchVehicles,
    restoreDocument: restoreDocumentMutation.mutate,
    pauseSnapshotListener: () => setIsSnapshotListenerActive(false),
    resumeSnapshotListener: () => setIsSnapshotListenerActive(true),
  }
}
